import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {LockGlyph} from '@/components/ui/icons'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useAuditExport, useStartAuditExport, type SgsstAuditExport} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {useAbrirEvidencia} from '@/hooks/sgsst/evidencias/use-evidence'

// Any operador can export: it reads the period's evidence, not an attestation, so no licencia gate
// or read-only latch. The build is idempotent server-side per health scope (with or without health documents).
export function useAuditExportJob(businessId: string, year: number) {
  const start = useStartAuditExport(businessId, year)
  const {data: job} = useAuditExport(businessId, year)
  const {abrir, isPending: abriendo} = useAbrirEvidencia()
  const custodia = useCapabilities(businessId).sgsst_custodiar_salud
  const status = job?.status
  const building = start.isPending || status === 'pendiente' || status === 'procesando'
  const ready = status === 'listo' && (job?.descarga === 'libre' || job?.descarga === 'con_registro')
  const completable = ready && custodia && job?.alcance === 'sin_salud'
  function launch() {
    start.mutate(undefined)
  }
  function download() {
    if (!job) return
    // Con documentos de salud la URL se firma al clic: la firma deja la fila de quien descarga.
    if (job.download_url) window.open(job.download_url, '_blank', 'noopener')
    else abrir(`/businesses/${businessId}/sgsst/${year}/export-auditoria/${job.id}/descarga`)
  }
  return {job, status, building, ready, completable, abriendo, launch, download}
}

export function AuditExportAviso({job}: {job: SgsstAuditExport | null | undefined}) {
  const {t} = useTranslation()
  if (job?.status !== 'listo') return null
  if (job.descarga === 'solo_custodia')
    return (
      <span className="flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="audit-export-reservado">
        <LockGlyph />
        {t('sgsst.export.reservado')}
      </span>
    )
  if (job.descarga === 'regenerar')
    return (
      <span className="text-[11px] text-muted-foreground" data-testid="audit-export-regenerar">
        {t('sgsst.export.regenerar')}
      </span>
    )
  return null
}

export function AuditExportLaunchButton({businessId, year}: {businessId: string; year: number}) {
  const {t} = useTranslation()
  const {building, ready, completable, launch} = useAuditExportJob(businessId, year)
  if (building || (ready && !completable)) return null
  return (
    <Button
      size="sm"
      variant="outline"
      data-testid={completable ? 'audit-export-completo' : 'audit-export'}
      onClick={launch}>
      {t(completable ? 'sgsst.export.completo' : 'sgsst.export.action')}
    </Button>
  )
}

export function AuditExportStatus({businessId, year}: {businessId: string; year: number}) {
  const {t} = useTranslation()
  const {job, status, building, ready, abriendo, download} = useAuditExportJob(businessId, year)
  const aviso = status === 'listo' && !ready
  if (!building && !ready && !aviso && status !== 'error') return null
  return (
    <div className="flex flex-col items-start gap-1 sm:items-end">
      {ready ? (
        <Button size="sm" variant="outline" disabled={abriendo} data-testid="audit-export-download" onClick={download}>
          {t('sgsst.export.download')}
        </Button>
      ) : building ? (
        <Button size="sm" variant="outline" disabled data-testid="audit-export-building">
          {t('sgsst.export.building')}
        </Button>
      ) : null}
      {building && job?.created_by && (
        <span className="font-mono text-[11px] text-muted-foreground" data-testid="audit-export-launched-by">
          {t('sgsst.export.launchedBy', {name: job.created_by})}
        </span>
      )}
      {status === 'error' && (
        <span className="font-mono text-[11px] text-destructive" data-testid="audit-export-error">
          {t('sgsst.export.error')}
        </span>
      )}
      <AuditExportAviso job={job} />
    </div>
  )
}
